import moment from 'moment';
import {
  DIMENSION_CATEGORIA,
  DIMENSION_FINALIDADE,
  DIMENSION_GERAL,
  DIMENSION_TIPO_VEICULO,
  DIMENSION_TO_API_FILTER_KEY,
  DIMENSION_TO_SEGMENTOS_KEY,
  SEGMENT_ALL,
  STATIC_SEGMENT_OPTIONS,
  getSegmentFilterLabel,
  isSegmentFilterActive,
} from './segmentConstants';

export const CATEGORIAS = {
  COMBUSTIVEL: 'Combustível',
  MANUTENCAO: 'Manutenção',
  PECAS: 'Peças',
  MULTAS: 'Multas',
  OUTROS: 'Outros',
};

/** Categorias que filtram a tabela de transações ao clicar no KPI. */
export const FILTERABLE_CATEGORIAS = Object.values(CATEGORIAS);

export const CATEGORIA_CHART_COLORS = {
  [CATEGORIAS.COMBUSTIVEL]: '#1976d2',
  [CATEGORIAS.MANUTENCAO]: '#ed6c02',
  [CATEGORIAS.PECAS]: '#9c27b0',
  [CATEGORIAS.MULTAS]: '#d32f2f',
  [CATEGORIAS.OUTROS]: '#757575',
};

export const CATEGORIA_STAT_COLORS = {
  [CATEGORIAS.COMBUSTIVEL]: 'info',
  [CATEGORIAS.MANUTENCAO]: 'warning',
  [CATEGORIAS.PECAS]: 'default',
  [CATEGORIAS.MULTAS]: 'error',
  [CATEGORIAS.OUTROS]: 'default',
};

export const EVOLUCAO_SERIES_KEYS = [
  { key: 'combustivel', label: CATEGORIAS.COMBUSTIVEL },
  { key: 'manutencao', label: CATEGORIAS.MANUTENCAO },
  { key: 'pecas', label: CATEGORIAS.PECAS },
  { key: 'multas', label: CATEGORIAS.MULTAS },
];

export const EMPTY_KPIS = {
  totalGasto: 0,
  combustivel: 0,
  manutencao: 0,
  pecas: 0,
  multas: 0,
  outros: 0,
  kmRodado: null,
  custoPorKm: null,
};

export const EMPTY_SEGMENTOS = {
  finalidade: [],
  tipoVeiculo: [],
  categoriaVeiculo: [],
};

export const EMPTY_REPORT = {
  veiculo: null,
  kpis: EMPTY_KPIS,
  evolucaoMensal: [],
  distribuicao: [],
  transacoes: [],
  rankingVeiculos: [],
  segmentos: EMPTY_SEGMENTOS,
};

export const getDefaultFilter = () => ({
  veiculoId: null,
  inicio: moment().startOf('year').valueOf(),
  fim: moment().endOf('day').valueOf(),
  dim: DIMENSION_GERAL,
  seg: SEGMENT_ALL,
});

export const FROTA_SCOPE_LABEL = 'Frota (todos os veículos)';

export const getScopeLabel = (veiculoOrPlaca) => {
  const placa =
    veiculoOrPlaca != null && typeof veiculoOrPlaca === 'object'
      ? veiculoOrPlaca.placa
      : veiculoOrPlaca;
  return placa ? `Veículo: ${placa}` : FROTA_SCOPE_LABEL;
};

export const isFleetScope = (filter) => !filter?.veiculoId;

export const formatPeriodLabel = (inicio, fim) => {
  if (inicio == null || fim == null) return '';
  const start = moment(inicio).format('DD/MM/YYYY');
  const end = moment(fim).format('DD/MM/YYYY');
  return `${start} – ${end}`;
};

export const formatMesLabel = (mes) => {
  if (!mes) return '—';
  const parsed = moment(mes, 'YYYY-MM', true);
  if (!parsed.isValid()) return mes;
  return parsed.format('MMM/YYYY');
};

export const formatBRL = (value) => {
  if (value == null || Number.isNaN(Number(value))) return '—';
  return Number(value).toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  });
};

export const formatKm = (value) => {
  if (value == null || Number.isNaN(Number(value))) return '—';
  return Number(value).toLocaleString('pt-BR');
};

export const normalizeFilterForApi = (filter) => {
  if (!filter || typeof filter !== 'object') return null;
  const body = {
    inicio: filter.inicio ?? null,
    fim: filter.fim ?? null,
  };
  if (filter.veiculoId) {
    body.veiculoId = filter.veiculoId;
    return body;
  }

  const dim = filter.dim || DIMENSION_GERAL;
  const seg = filter.seg || SEGMENT_ALL;
  if (isSegmentFilterActive(dim, seg)) {
    const apiKey = DIMENSION_TO_API_FILTER_KEY[dim];
    if (apiKey) {
      body[apiKey] = [seg];
    }
  }

  return body;
};

export const normalizeVeiculo = (data) => {
  if (!data || typeof data !== 'object') return null;
  return {
    id: data.id ?? null,
    placa: data.placa ?? null,
    modelo: data.modelo ?? null,
    fabricante: data.fabricante ?? null,
    ano: data.ano ?? null,
    categoria: data.categoria ?? null,
    tipoVeiculo: data.tipoVeiculo ?? null,
    cor: data.cor ?? null,
    combustivel: data.combustivel ?? null,
    numFrota: data.numFrota ?? null,
    kmAtual: data.kmAtual != null ? Number(data.kmAtual) : null,
  };
};

const normalizeSegmentItem = (item) => ({
  key: item.key ?? item.id ?? item.value ?? item.codigo ?? null,
  label: item.label ?? item.nome ?? item.value ?? '—',
  total: Number(item.total) || 0,
  percentual: item.percentual != null ? Number(item.percentual) : null,
  quantidadeVeiculos: Number(item.quantidadeVeiculos) || 0,
  combustivel: Number(item.combustivel) || 0,
  manutencao: Number(item.manutencao) || 0,
  pecas: Number(item.pecas) || 0,
  multas: Number(item.multas) || 0,
  outros: Number(item.outros) || 0,
});

const normalizeSegmentos = (segmentos) => {
  if (!segmentos || typeof segmentos !== 'object') {
    return { ...EMPTY_SEGMENTOS };
  }
  return {
    finalidade: Array.isArray(segmentos.finalidade)
      ? segmentos.finalidade.map(normalizeSegmentItem)
      : [],
    tipoVeiculo: Array.isArray(segmentos.tipoVeiculo)
      ? segmentos.tipoVeiculo.map(normalizeSegmentItem)
      : [],
    categoriaVeiculo: Array.isArray(segmentos.categoriaVeiculo)
      ? segmentos.categoriaVeiculo.map(normalizeSegmentItem)
      : [],
  };
};

const normalizeRankingVeiculoItem = (item) => ({
  veiculoId: item.veiculoId ?? null,
  placa: item.placa ?? null,
  total: Number(item.total) || 0,
  percentualFrota: item.percentualFrota != null ? Number(item.percentualFrota) : null,
  combustivel: Number(item.combustivel) || 0,
  manutencao: Number(item.manutencao) || 0,
  pecas: Number(item.pecas) || 0,
  multas: Number(item.multas) || 0,
  outros: Number(item.outros) || 0,
  finalidade: item.finalidade ?? item.finalidadeKey ?? null,
  finalidadeLabel: item.finalidadeLabel ?? null,
  tipoVeiculo: item.tipoVeiculo ?? item.tipoVeiculoKey ?? null,
  tipoVeiculoLabel: item.tipoVeiculoLabel ?? null,
  categoriaVeiculo: item.categoriaVeiculo ?? item.categoriaVeiculoKey ?? null,
  categoriaVeiculoLabel: item.categoriaVeiculoLabel ?? null,
});

const DIMENSION_RANKING_FIELDS = {
  [DIMENSION_FINALIDADE]: {
    key: 'finalidade',
    label: 'finalidadeLabel',
  },
  [DIMENSION_TIPO_VEICULO]: {
    key: 'tipoVeiculo',
    label: 'tipoVeiculoLabel',
  },
  [DIMENSION_CATEGORIA]: {
    key: 'categoriaVeiculo',
    label: 'categoriaVeiculoLabel',
    fallbackKey: 'categoria',
  },
};

const DIMENSION_VEICULO_FIELDS = {
  [DIMENSION_FINALIDADE]: 'finalidade',
  [DIMENSION_TIPO_VEICULO]: 'tipoVeiculo',
  [DIMENSION_CATEGORIA]: 'categoria',
};

const getTipoVeiculoLabel = (tipo) => {
  if (tipo === 'ELETROPONTES') return 'Eletropontes';
  if (tipo === 'PARTICULAR') return 'Particular';
  if (tipo === 'ALUGADO') return 'Alugado';
  return tipo || '';
};

const getCategoriaVeiculoLabel = (key) => {
  const found = STATIC_SEGMENT_OPTIONS[DIMENSION_CATEGORIA]?.find((o) => o.key === key);
  return found?.label || key || '';
};

const getSegmentLabel = (dimension, key, finalidadeLabels = {}) => {
  if (dimension === DIMENSION_FINALIDADE) return finalidadeLabels[key] || key;
  if (dimension === DIMENSION_TIPO_VEICULO) return getTipoVeiculoLabel(key);
  if (dimension === DIMENSION_CATEGORIA) return getCategoriaVeiculoLabel(key);
  return key;
};

const RANKING_CATEGORY_KEYS = ['combustivel', 'manutencao', 'pecas', 'multas', 'outros'];

/** Categorias tipadas do backend (tipo_nota / tipo_historico) → chave do gráfico empilhado. */
const TIPO_CATEGORIA_MAP = {
  ABASTECIMENTO: 'combustivel',
  MANUTENCAO_VEICULO: 'manutencao',
  COMPRA_MANUTENCAO_VEICULAR: 'manutencao',
  MANUTENCAO: 'manutencao',
  PECAS: 'pecas',
  PECA: 'pecas',
  MULTA: 'multas',
  AUTUACAO: 'multas',
  PEDAGIO: 'outros',
  PEDAGIOS: 'outros',
  REPASSE_COLABORADOR: 'outros',
  VENDA_TROCA: 'outros',
};

export const hasRealCostBreakdown = (row) => {
  if (!row) return false;
  return (
    (Number(row.combustivel) || 0) +
    (Number(row.manutencao) || 0) +
    (Number(row.pecas) || 0) +
    (Number(row.multas) || 0) > 0
  );
};

/** Último recurso: só quando não há breakdown real e existe total. */
export const ensureCostBreakdown = (row) => {
  if (!row || typeof row !== 'object') return row;
  const next = { ...row };
  const sum = RANKING_CATEGORY_KEYS.reduce(
    (acc, key) => acc + (Number(next[key]) || 0),
    0,
  );
  const total = Number(next.total) || 0;

  if (total > 0 && !hasRealCostBreakdown(next) && sum === 0) {
    next.outros = total;
  } else if (hasRealCostBreakdown(next) && total === 0) {
    next.total = sum;
  }

  return next;
};

const normalizePlacaKey = (placa) => {
  if (!placa) return null;
  return String(placa).trim().toUpperCase();
};

export const buildVeiculosByPlaca = (veiculosOptions) => {
  const map = {};
  (veiculosOptions || []).forEach((veiculo) => {
    const placa = veiculo?.placa?.trim().toUpperCase();
    if (placa) map[placa] = veiculo;
  });
  return map;
};

const emptySegmentAggregate = (key, label) => ({
  key,
  label,
  total: 0,
  percentual: null,
  quantidadeVeiculos: 0,
  combustivel: 0,
  manutencao: 0,
  pecas: 0,
  multas: 0,
  outros: 0,
});

/** Agrega ranking de veículos por dimensão para popular chips quando a API não envia segmentos. */
export const buildSegmentOptionsFromRanking = (ranking, dimension) => {
  const fields = DIMENSION_RANKING_FIELDS[dimension];
  if (!fields || !Array.isArray(ranking) || ranking.length === 0) return [];

  const byKey = {};

  ranking.forEach((item) => {
    const segKey = item[fields.key] ?? item[fields.fallbackKey] ?? item[fields.label];
    const label = item[fields.label] ?? item[fields.key] ?? item[fields.fallbackKey];
    if (!segKey) return;

    if (!byKey[segKey]) {
      byKey[segKey] = emptySegmentAggregate(segKey, label || segKey);
    }

    byKey[segKey].total += item.total;
    byKey[segKey].combustivel += item.combustivel;
    byKey[segKey].manutencao += item.manutencao;
    byKey[segKey].pecas += item.pecas;
    byKey[segKey].multas += item.multas;
    byKey[segKey].outros += item.outros;
    byKey[segKey].quantidadeVeiculos += 1;
  });

  return Object.values(byKey).sort((a, b) => b.total - a.total);
};

/**
 * Agrega transações por dimensão usando cadastro de veículos (placa → finalidade/tipo/categoria).
 */
export const buildSegmentOptionsFromTransacoes = (
  transacoes,
  veiculosByPlaca,
  dimension,
  finalidadeLabels = {},
) => {
  const veiculoField = DIMENSION_VEICULO_FIELDS[dimension];
  if (!veiculoField || !veiculosByPlaca || !transacoes?.length) return [];

  const byKey = {};
  const placasPorSegmento = {};

  transacoes.forEach((tx) => {
    const placa = (tx.placa || tx.veiculoPlaca || '').trim().toUpperCase();
    if (!placa) return;

    const veiculo = veiculosByPlaca[placa];
    if (!veiculo) return;

    let segKey = veiculo[veiculoField];
    if (!segKey) {
      segKey = dimension === DIMENSION_FINALIDADE ? '(sem)' : null;
      if (!segKey) return;
    }

    const catKey = normalizeTransacaoCategoria(
      tx.categoria ?? tx.tipoNota ?? tx.tipoHistorico ?? tx.tipo,
    );
    const valor = Number(tx.valor) || 0;
    if (valor <= 0) return;

    if (!byKey[segKey]) {
      byKey[segKey] = emptySegmentAggregate(
        segKey,
        getSegmentLabel(dimension, segKey, finalidadeLabels),
      );
      placasPorSegmento[segKey] = new Set();
    }

    byKey[segKey][catKey] += valor;
    byKey[segKey].total += valor;
    placasPorSegmento[segKey].add(placa);
  });

  return Object.values(byKey)
    .map((item) => ({
      ...item,
      quantidadeVeiculos: placasPorSegmento[item.key]?.size || 0,
    }))
    .sort((a, b) => b.total - a.total);
};

const mergeSegmentWithTransacoes = (apiRow, txRow) => {
  if (!txRow) {
    return hasRealCostBreakdown(apiRow) ? apiRow : ensureCostBreakdown(apiRow);
  }

  return {
    ...apiRow,
    label: apiRow.label || txRow.label,
    combustivel: txRow.combustivel,
    manutencao: txRow.manutencao,
    pecas: txRow.pecas,
    multas: txRow.multas,
    outros: txRow.outros,
    total: txRow.total || apiRow.total,
    quantidadeVeiculos: txRow.quantidadeVeiculos ?? apiRow.quantidadeVeiculos,
  };
};

const mergeApiSegmentsWithTransacoes = (fromApi, fromTransacoes) => {
  const txByKey = {};
  const txByLabel = {};
  (fromTransacoes || []).forEach((item) => {
    if (item?.key != null) txByKey[String(item.key)] = item;
    if (item?.label) txByLabel[String(item.label).trim().toLowerCase()] = item;
  });

  const resolveTxRow = (apiRow) => {
    if (apiRow?.key != null && txByKey[String(apiRow.key)]) {
      return txByKey[String(apiRow.key)];
    }
    if (apiRow?.label) {
      return txByLabel[String(apiRow.label).trim().toLowerCase()] || null;
    }
    return null;
  };

  const merged = (fromApi || []).map((apiRow) =>
    mergeSegmentWithTransacoes(apiRow, resolveTxRow(apiRow)),
  );
  const seenKeys = new Set(merged.map((row) => String(row.key)));

  (fromTransacoes || []).forEach((txRow) => {
    if (txRow?.key == null || seenKeys.has(String(txRow.key))) return;
    merged.push(txRow);
    seenKeys.add(String(txRow.key));
  });

  return merged.sort((a, b) => b.total - a.total);
};

const mergeWithStaticSegmentOptions = (dynamic, staticList) => {
  const byKey = {};

  (staticList || []).forEach((opt) => {
    byKey[opt.key] = emptySegmentAggregate(opt.key, opt.label);
  });

  (dynamic || []).forEach((item) => {
    if (!item?.key) return;
    const existing = byKey[item.key] || emptySegmentAggregate(item.key, item.label || item.key);
    byKey[item.key] = {
      ...existing,
      ...item,
      label: item.label || existing.label,
    };
  });

  return Object.values(byKey).sort((a, b) => b.total - a.total);
};

/**
 * Resolve opções de chips: API/cache → merge(ranking + estáticos).
 * `extraStaticOptions` — ex.: finalidades de GET options/VEICULO_FINALIDADES.
 */
export const resolveSegmentChipOptions = ({
  dimension,
  apiSegmentos,
  cacheOptions,
  ranking,
  transacoes,
  veiculosByPlaca,
  finalidadeLabels,
  extraStaticOptions,
}) => {
  const segmentKey = DIMENSION_TO_SEGMENTOS_KEY[dimension];
  const fromApi = (cacheOptions && cacheOptions.length > 0)
    ? cacheOptions
    : (apiSegmentos?.[segmentKey] || []);

  const fromTransacoes = buildSegmentOptionsFromTransacoes(
    transacoes,
    veiculosByPlaca,
    dimension,
    finalidadeLabels,
  );

  if (fromApi.length > 0) {
    const merged = mergeApiSegmentsWithTransacoes(fromApi, fromTransacoes);
    const staticList = extraStaticOptions || STATIC_SEGMENT_OPTIONS[dimension];
    if (staticList?.length) {
      return mergeWithStaticSegmentOptions(merged, staticList);
    }
    return merged;
  }

  const fromRanking = buildSegmentOptionsFromRanking(ranking, dimension);
  const dynamic = fromTransacoes.length > 0 ? fromTransacoes : fromRanking;
  const staticList = extraStaticOptions || STATIC_SEGMENT_OPTIONS[dimension];

  if (staticList?.length) {
    return mergeWithStaticSegmentOptions(dynamic, staticList);
  }

  return dynamic;
};

/** Itens com custo > 0 para gráficos/tabelas comparativas. */
export const filterSegmentsWithCost = (items) =>
  (items || []).filter((item) => Number(item.total) > 0);

export const normalizeBiResponse = (data) => {
  if (!data || typeof data !== 'object') {
    return EMPTY_REPORT;
  }

  const kpis = data.kpis && typeof data.kpis === 'object' ? data.kpis : {};
  const transacoes = Array.isArray(data.transacoes)
    ? data.transacoes.map((row) => ({
      ...row,
      placa: row.placa ?? row.veiculoPlaca ?? null,
    }))
    : [];
  const rankingVeiculos = Array.isArray(data.rankingVeiculos)
    ? data.rankingVeiculos.map(normalizeRankingVeiculoItem)
    : [];

  return {
    veiculo: normalizeVeiculo(data.veiculo),
    kpis: {
      totalGasto: Number(kpis.totalGasto) || 0,
      combustivel: Number(kpis.combustivel) || 0,
      manutencao: Number(kpis.manutencao) || 0,
      pecas: Number(kpis.pecas) || 0,
      multas: Number(kpis.multas) || 0,
      outros: Number(kpis.outros) || 0,
      kmRodado: kpis.kmRodado != null ? Number(kpis.kmRodado) : null,
      custoPorKm: kpis.custoPorKm != null ? Number(kpis.custoPorKm) : null,
    },
    evolucaoMensal: Array.isArray(data.evolucaoMensal) ? data.evolucaoMensal : [],
    distribuicao: Array.isArray(data.distribuicao) ? data.distribuicao : [],
    transacoes,
    rankingVeiculos,
    segmentos: normalizeSegmentos(data.segmentos),
  };
};

export const mapEvolucaoToDataset = (items) =>
  (items || []).map((item) => ({
    mes: item.mes,
    mesLabel: formatMesLabel(item.mes),
    combustivel: Number(item.combustivel) || 0,
    manutencao: Number(item.manutencao) || 0,
    pecas: Number(item.pecas) || 0,
    multas: Number(item.multas) || 0,
    outros: Number(item.outros) || 0,
  }));

export const evolucaoHasData = (dataset) =>
  (dataset || []).some((row) =>
    EVOLUCAO_SERIES_KEYS.some(({ key }) => Number(row[key]) > 0),
  );

export const buildEvolucaoStackedSeries = () =>
  EVOLUCAO_SERIES_KEYS.map(({ key, label }) => ({
    dataKey: key,
    label,
    stack: 'custos',
    color: CATEGORIA_CHART_COLORS[label],
    valueFormatter: (value) => formatBRL(value),
  }));

export const RANKING_TOP_N = 10;
export const PARETO_THRESHOLD = 80;

export const RANKING_SERIES_KEYS = [
  { key: 'combustivel', label: CATEGORIAS.COMBUSTIVEL },
  { key: 'manutencao', label: CATEGORIAS.MANUTENCAO },
  { key: 'pecas', label: CATEGORIAS.PECAS },
  { key: 'multas', label: CATEGORIAS.MULTAS },
  { key: 'outros', label: CATEGORIAS.OUTROS },
];

const normalizeTransacaoCategoria = (categoria) => {
  if (!categoria) return 'outros';

  const enumKey = String(categoria).toUpperCase().trim();
  if (TIPO_CATEGORIA_MAP[enumKey]) {
    return TIPO_CATEGORIA_MAP[enumKey];
  }

  if (Object.values(CATEGORIAS).includes(categoria)) {
    switch (categoria) {
      case CATEGORIAS.COMBUSTIVEL:
        return 'combustivel';
      case CATEGORIAS.MANUTENCAO:
        return 'manutencao';
      case CATEGORIAS.PECAS:
        return 'pecas';
      case CATEGORIAS.MULTAS:
        return 'multas';
      default:
        return 'outros';
    }
  }

  const norm = enumKey
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');

  if (norm.includes('ABASTEC') || norm.includes('COMBUST')) return 'combustivel';
  if (norm.includes('MANUT')) return 'manutencao';
  if (norm.includes('PECA')) return 'pecas';
  if (norm.includes('MULTA') || norm.includes('AUTUAC')) return 'multas';
  if (norm.includes('PEDAG')) return 'outros';
  return 'outros';
};

const emptyRankingRow = (placa) => ({
  placa,
  combustivel: 0,
  manutencao: 0,
  pecas: 0,
  multas: 0,
  outros: 0,
  total: 0,
});

/** Agrega transações da visão frota por placa, ordenado por custo total desc. */
export const buildRankingFromTransacoes = (transacoes) => {
  const byPlaca = {};

  (transacoes || []).forEach((row) => {
    const placa = row.placa || row.veiculoPlaca;
    if (!placa) return;

    const catKey = normalizeTransacaoCategoria(
      row.categoria ?? row.tipoNota ?? row.tipoHistorico ?? row.tipo,
    );
    const valor = Number(row.valor) || 0;

    if (!byPlaca[placa]) {
      byPlaca[placa] = emptyRankingRow(placa);
    }

    byPlaca[placa][catKey] += valor;
    byPlaca[placa].total += valor;
  });

  return Object.values(byPlaca).sort((a, b) => b.total - a.total);
};

const mergeRankingRowWithTransacoes = (apiRow, txRow) => {
  if (!txRow) {
    return hasRealCostBreakdown(apiRow) ? apiRow : ensureCostBreakdown(apiRow);
  }

  return {
    ...apiRow,
    combustivel: txRow.combustivel,
    manutencao: txRow.manutencao,
    pecas: txRow.pecas,
    multas: txRow.multas,
    outros: txRow.outros,
    total: txRow.total || apiRow.total,
    placa: apiRow.placa || txRow.placa,
  };
};

export const getTopRanking = (ranking, limit = RANKING_TOP_N) =>
  (ranking || []).slice(0, limit);

/** Quantos veículos concentram pelo menos PARETO_THRESHOLD% do custo total. */
export const computeParetoInsight = (ranking, totalGasto) => {
  const total = Number(totalGasto) || 0;
  const vehicles = ranking || [];
  const totalVehicles = vehicles.length;

  if (total <= 0 || totalVehicles === 0) {
    return { topCount: 0, topPercent: 0, totalVehicles: 0, totalGasto: total };
  }

  let cumulative = 0;
  let topCount = 0;

  for (let i = 0; i < vehicles.length; i += 1) {
    cumulative += vehicles[i].total;
    topCount = i + 1;
    if ((cumulative / total) * 100 >= PARETO_THRESHOLD) break;
  }

  return {
    topCount,
    topPercent: (cumulative / total) * 100,
    totalVehicles,
    totalGasto: total,
  };
};

export const formatPercent = (value) => {
  if (value == null || Number.isNaN(Number(value))) return '—';
  return `${Number(value).toLocaleString('pt-BR', { maximumFractionDigits: 1 })}%`;
};

export const mapRankingToChartDataset = (ranking) =>
  getTopRanking(ranking)
    .filter((item) => Number(item.total) > 0)
    .map((item) => ({
      label: item.placa || '—',
      placa: item.placa,
      combustivel: item.combustivel,
      manutencao: item.manutencao,
      pecas: item.pecas,
      multas: item.multas,
      outros: item.outros,
      total: item.total,
    }));

export const buildRankingStackedSeries = () =>
  RANKING_SERIES_KEYS.map(({ key, label }) => ({
    dataKey: key,
    label,
    stack: 'custos',
    color: CATEGORIA_CHART_COLORS[label],
    valueFormatter: (value) => formatBRL(value),
  }));

/** Prioriza breakdown das transações; usa API para ordenação/metadados. */
export const resolveRanking = (rankingVeiculos, transacoes) => {
  const fromTx = buildRankingFromTransacoes(transacoes);
  const txByPlaca = {};

  fromTx.forEach((row) => {
    const key = normalizePlacaKey(row.placa);
    if (key) txByPlaca[key] = row;
  });

  if (!Array.isArray(rankingVeiculos) || rankingVeiculos.length === 0) {
    return fromTx;
  }

  const merged = [];
  const seenPlacas = new Set();

  rankingVeiculos.forEach((item) => {
    const apiRow = normalizeRankingVeiculoItem(item);
    const key = normalizePlacaKey(apiRow.placa);
    const txRow = key ? txByPlaca[key] : null;
    merged.push(mergeRankingRowWithTransacoes(apiRow, txRow));
    if (key) seenPlacas.add(key);
  });

  fromTx.forEach((txRow) => {
    const key = normalizePlacaKey(txRow.placa);
    if (key && !seenPlacas.has(key)) {
      merged.push(txRow);
    }
  });

  return merged.sort((a, b) => b.total - a.total);
};

export const mapSegmentToChartDataset = (items) =>
  filterSegmentsWithCost(items).map((item) => {
    const row = hasRealCostBreakdown(item) ? item : ensureCostBreakdown(item);
    return {
      label: row.label || '—',
      key: row.key,
      total: row.total,
      combustivel: row.combustivel,
      manutencao: row.manutencao,
      pecas: row.pecas,
      multas: row.multas,
      outros: row.outros,
    };
  });

export const isPeriodInvalid = (inicio, fim) => {
  if (inicio == null || fim == null) return false;
  return moment(inicio).isAfter(moment(fim));
};

const URL_DATE_FORMAT = 'YYYY-MM-DD';

const VALID_DIMENSIONS = new Set([
  DIMENSION_GERAL,
  DIMENSION_FINALIDADE,
  DIMENSION_TIPO_VEICULO,
  DIMENSION_CATEGORIA,
]);

/** Lê filtros da query string (?veiculoId=&inicio=&fim=&dim=&seg=). Datas em YYYY-MM-DD. */
export const parseFilterFromSearch = (search, defaultFilter) => {
  const q = new URLSearchParams(search);
  const filter = {
    ...defaultFilter,
    veiculoId: null,
  };

  const veiculoId = q.get('veiculoId');
  if (veiculoId) {
    filter.veiculoId = veiculoId;
  }

  const inicio = q.get('inicio');
  if (inicio) {
    const parsed = moment(inicio, URL_DATE_FORMAT, true);
    if (parsed.isValid()) {
      filter.inicio = parsed.startOf('day').valueOf();
    }
  }

  const fim = q.get('fim');
  if (fim) {
    const parsed = moment(fim, URL_DATE_FORMAT, true);
    if (parsed.isValid()) {
      filter.fim = parsed.endOf('day').valueOf();
    }
  }

  const dim = q.get('dim');
  if (dim && VALID_DIMENSIONS.has(dim)) {
    filter.dim = dim;
  }

  const seg = q.get('seg');
  if (seg) {
    filter.seg = seg;
  }

  return filter;
};

/** Serializa filtros para query string compartilhável. */
export const buildSearchFromFilter = (filter) => {
  const q = new URLSearchParams();
  if (filter?.veiculoId) {
    q.set('veiculoId', filter.veiculoId);
  }
  if (filter?.inicio != null) {
    q.set('inicio', moment(filter.inicio).format(URL_DATE_FORMAT));
  }
  if (filter?.fim != null) {
    q.set('fim', moment(filter.fim).format(URL_DATE_FORMAT));
  }
  if (filter?.dim && filter.dim !== DIMENSION_GERAL) {
    q.set('dim', filter.dim);
    if (filter.seg && filter.seg !== SEGMENT_ALL) {
      q.set('seg', filter.seg);
    }
  }
  return q.toString();
};

export const filtersEqual = (a, b) => {
  if (!a || !b) return false;
  return (
    (a.veiculoId || null) === (b.veiculoId || null) &&
    a.inicio === b.inicio &&
    a.fim === b.fim &&
    (a.dim || DIMENSION_GERAL) === (b.dim || DIMENSION_GERAL) &&
    (a.seg || SEGMENT_ALL) === (b.seg || SEGMENT_ALL)
  );
};

export const searchMatchesFilter = (search, filter) =>
  buildSearchFromFilter(filter) === search.replace(/^\?/, '');

/** URL sem query string (ex.: entrada pelo menu lateral). */
export const hasFilterSearchParams = (search) =>
  Boolean(search && search.replace(/^\?/, '').trim().length > 0);

// --- Export PDF ---

export const SECAO_RELATORIO_VEICULO = {
  KPIS: 'KPIS',
  DISTRIBUICAO: 'DISTRIBUICAO',
  EVOLUCAO_MENSAL: 'EVOLUCAO_MENSAL',
  RANKING: 'RANKING',
  SEGMENTOS: 'SEGMENTOS',
  DETALHADO: 'DETALHADO',
};

export const SECAO_RELATORIO_VEICULO_OPTIONS = [
  {
    value: SECAO_RELATORIO_VEICULO.KPIS,
    label: 'Indicadores (KPIs)',
    description: 'Total gasto, combustível, manutenção, peças, multas, km rodado e custo/km.',
  },
  {
    value: SECAO_RELATORIO_VEICULO.DISTRIBUICAO,
    label: 'Distribuição por categoria',
    description: 'Gasto por categoria (combustível, manutenção, peças…) com percentual do total.',
  },
  {
    value: SECAO_RELATORIO_VEICULO.EVOLUCAO_MENSAL,
    label: 'Evolução mensal',
    description: 'Valores mês a mês, detalhados por categoria de custo.',
  },
  {
    value: SECAO_RELATORIO_VEICULO.RANKING,
    label: 'Ranking de veículos',
    description: 'Veículos ordenados por total gasto no período.',
    disableWhenVeiculoFilter: true,
  },
  {
    value: SECAO_RELATORIO_VEICULO.SEGMENTOS,
    label: 'Segmentos',
    description: 'Comparativo por finalidade, proprietário e categoria de veículo.',
    disableWhenVeiculoFilter: true,
  },
  {
    value: SECAO_RELATORIO_VEICULO.DETALHADO,
    label: 'Extrato detalhado (transações)',
    description: 'Cada lançamento em linha — data, placa, categoria, valor e descrição.',
  },
];

const resolveSegmentLabelForPreview = (filter, segmentLabel) => {
  if (!filter || !isSegmentFilterActive(filter.dim, filter.seg)) return null;
  if (segmentLabel) return segmentLabel;

  const dim = filter.dim;
  const seg = filter.seg;
  const staticOptions = STATIC_SEGMENT_OPTIONS[dim];
  const match = staticOptions?.find((o) => o.key === seg);
  return match?.label || seg;
};

export const isExportSecaoDisabled = (secaoValue, applyFilters, filter) =>
  Boolean(
    applyFilters
    && filter?.veiculoId
    && SECAO_RELATORIO_VEICULO_OPTIONS.find((o) => o.value === secaoValue)
      ?.disableWhenVeiculoFilter,
  );

export const getDefaultExportSecoes = (applyFilters, filter) =>
  SECAO_RELATORIO_VEICULO_OPTIONS.filter(
    (o) => o.value !== SECAO_RELATORIO_VEICULO.DETALHADO
      && !isExportSecaoDisabled(o.value, applyFilters, filter),
  ).map((o) => o.value);

export const buildDefaultExportApiBody = () => normalizeFilterForApi(getDefaultFilter());

const buildExportFilterPreviewChips = ({
  filter,
  activeCategoria = null,
  veiculoPlaca = null,
  segmentLabel = null,
}) => {
  if (!filter) return [];

  const chips = [];
  const periodo = formatPeriodLabel(filter.inicio, filter.fim);
  chips.push(periodo ? `Período: ${periodo}` : 'Período completo');

  if (filter.veiculoId) {
    chips.push(veiculoPlaca ? `Veículo: ${veiculoPlaca}` : 'Veículo: Selecionado');
  } else {
    chips.push(FROTA_SCOPE_LABEL);
  }

  if (activeCategoria) {
    chips.push(`Categoria: ${activeCategoria}`);
  }

  const segmentPreviewLabel = getSegmentFilterLabel(
    filter.dim,
    filter.seg,
    resolveSegmentLabelForPreview(filter, segmentLabel),
  );
  if (segmentPreviewLabel) {
    chips.push(segmentPreviewLabel);
  }

  return chips;
};

export const buildExportFilterPreview = ({
  filter,
  activeCategoria = null,
  applyFilters = true,
  veiculoPlaca = null,
  segmentLabel = null,
}) => {
  if (!applyFilters) {
    return buildExportFilterPreviewChips({
      filter: getDefaultFilter(),
    });
  }

  return buildExportFilterPreviewChips({
    filter,
    activeCategoria,
    veiculoPlaca,
    segmentLabel,
  });
};

export const buildExportPdfPayload = ({
  filter,
  secoes,
  activeCategoria = null,
  applyFilters = true,
}) => {
  if (!secoes?.length) return null;

  const body = applyFilters
    ? normalizeFilterForApi(filter)
    : buildDefaultExportApiBody();

  if (!body) return null;

  if (applyFilters && activeCategoria) {
    body.categorias = [activeCategoria];
  }

  return { ...body, secoes };
};
